import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as yup from 'yup'
import {
  faMemoCircleInfo,
  faUser,
  faAwardSimple,
  faList,
  faContainerStorage,
  faUser as partyIcon,
  faCashRegister as amountIcon,
  faLandmarkDome as governmentIcon,
  faArrowRightToBracket as submissionIcon,
} from '@fortawesome/pro-regular-svg-icons';
import DataTableSectionWithUpload from 'src/components/era-package/forms/DataTableSectionWithUpload';

import {
  header
} from 'src/views/abi/fields/duty-calculation';
import AbiDutyCalculationApiUrls from "../../../apiUrls/AbiDutyCalculationApiUrls";
import DutyCalculationOption from './DutyCalculationOption';




export const useDutyCalculationForm = (lineData, setLineData) => {
  const key = 'line';
  const [loading, setLoading] = useState(true);

  const handleUploadSuccess = useCallback(() => {
    console.log("Custom post upload logic for duty calculation");
  }, []);

  const fetchDutyCalculations = async () => {
    const response = await fetch(`${AbiDutyCalculationApiUrls.GET_FILE_UPLOAD}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
      },
    });
    const json = await response.json();
    return json.data;
  };

  const handleApiCall = useCallback(async (file, formData, formMethods) => {
    
    const freightSplitOptionMap = {
        'Split by weight': 'weight',
        'Split by value': 'value',
    };
    
    const totalFreightCharges = formData?.totalFreightCharges;
    const freightSplitOption = freightSplitOptionMap[formData?.totalFreightChargesSplitOption] ?? 'weight';
    const calType = 'template';
    const mpfCalculation = formData?.mpfCalculation ?? true;
    const hmfCalculation = formData?.hmfCalculation ?? true;
	const apiFormData = new FormData();
	
    apiFormData.append('file', file);
    apiFormData.append('totalFreightCharges', totalFreightCharges);
    apiFormData.append('freightSplitOption', freightSplitOption);
    apiFormData.append('calType', calType);
    apiFormData.append('mpfCalculation', mpfCalculation);
    apiFormData.append('hmfCalculation', hmfCalculation);
    try {
      const response = await fetch(AbiDutyCalculationApiUrls.UPLOAD, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: apiFormData
      });

      if (!response.ok) {
        throw new Error('Error uploading file');
      }

      const result = await response.json();
      handleRefresh();
    } catch (error) {
      throw new Error('Error uploading file');
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
        setLoading(true);
      const lineItems = await fetchDutyCalculations();
	  setLineData(lineItems);
      setLoading(false);
	  return lineItems;
    } catch (error) {
      console.error('Error refreshing line items:', error);
      setLoading(false);
    }
  }, [setLineData]);

  const handleDeleteRow = useCallback(async (id) => {
    const formData = new FormData();
    formData.append('id', id);

    try {
      const response = await fetch(AbiDutyCalculationApiUrls.DELETE, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Error deleting file');
      }
      const result = await response.json();
      console.log('File deleted successfully:', result);
      return result.data;
    } catch (error) {
      throw new Error('Error deleting file');
    }
  }, []);

  const formSections = useMemo(() => [
    {
      title: 'Duty Calculation',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table', name: `${key}`,
      tableComponent: DataTableSectionWithUpload,
      disableEdit: true,
      tableFields: header,
      onUploadSuccess: handleUploadSuccess,
      handleApiCall: handleApiCall,
      handleRefresh: handleRefresh,
      handleDeleteRow: handleDeleteRow,
      uploadFormComponent: <DutyCalculationOption />,
    }
  ], [handleApiCall, handleUploadSuccess, handleRefresh, handleDeleteRow]);

  return {
    name: key,
    formSections,
    schema: baseObj => yup.array().nullable().default([]),
    handleRefresh,
    loading
  }
}



const LineItemFormComponent = () => {
  const methods = useForm();
  const [lineData, setLineData] = useState([]);
  const { formSections, handleRefresh, loading } = useDutyCalculationForm(lineData, setLineData);

  const statusMap = {
    P: 'Processing',
    E: 'Error',
    S: 'Success',
  };

  useEffect(() => {
    const loadData = async () => {
      await handleRefresh();
    };
    loadData();
  }, [handleRefresh]);
  

const handleFileDownload = async (fileId, fileType, fileName) => {
  try {
    const response = await fetch(AbiDutyCalculationApiUrls.DOWNLOAD_FILE, {
      method: 'POST',
      headers: {
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      },
      body: new URLSearchParams({
        fileId: fileId.toString(),
        fileType: fileType,
      }),
    });

    if (!response.ok) throw new Error('Download failed');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName || 'download.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    alert('Failed to download file.');
    console.error(error);
  }
};

  


const columns = [
  {
    accessorKey: 'rowNumber',
    header: 'No',
    cell: ({ row }) => row.index + 1,
  },
  ...header.map(field => {
    if (field.name === 'message') {
      return {
        accessorKey: field.name,
        header: field.label,
        cell: ({ row }) => {
          const message = row.original?.message || '';
          const lines = message.split(';').map((line, i) => (
            <div key={i}>{line.trim()}</div>
          ));
          return (
            <Tooltip title={<>{lines}</>} arrow placement="top-start">
              <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                Hover to view
              </span>
            </Tooltip>
          );
        },
      };
    }
    else if (field.name === 'downloadFileName' || field.name === 'uploadFileName') {
      return {
        accessorKey: field.name,
        header: field.label,
        cell: ({ row }) => {
          const fileName = row.original?.[field.name];
          const fileId = row.original?.id;
          const fileType = field.name === 'downloadFileName' ? 'download' : 'upload';

          return (
            <span
              onClick={() => handleFileDownload(fileId, fileType, fileName)}
              style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {fileName}
            </span>
          );
        },
      };
    }
    else if (field.name === 'completedDate' || field.name === 'createdDate') {
        return {
            accessorKey: field.name,
            header: field.label,
            cell: ({ row }) => {
                const date = row.original?.[field.name];
                if (!date) return '-';

                return new Date(date).toLocaleString()
            },
        };
    }
    else if (field.name === 'status') {
        return {
            accessorKey: field.name,
            header: field.label,
            cell: ({ row }) => {
                const value = row.original?.[field.name];
                return statusMap[value] || value || '-';
            },
        };
    }


    return {
      accessorKey: field.name,
      header: field.label,
    };
  }),
];


return (
    <FormProvider {...methods}> {}
      <div>
        {loading ? <p>Loading...</p> : (
          formSections.map(section => {
            const TableComponent = section.tableComponent;
            return (
              <div key={section.title}>
                <h3>{section.title}</h3>
                <TableComponent
                  title={section.title}
                  fields={section.tableFields}
                  columns={columns} 
                  name={section.name}
                  data={lineData}
                  handleApiCall={section.handleApiCall}
                  handleRefresh={section.handleRefresh}
                  handleClearAll={section.handleClearAll}
                  filesUnderProcessing={section.filesUnderProcessing}
                  handleDeleteRow={section.handleDeleteRow}
                  onUploadSuccess={section.onUploadSuccess}
                  uploadFormComponent={section.uploadFormComponent}
                  disableEdit={section.disableEdit}
                  optionsMap={{}}                   
                   

                />
 
              </div>
            );
          })
        )}
      </div>
    </FormProvider>
  );

};


export default LineItemFormComponent;
